/**
 * The stacked-point picker (spec 5.3): when a click lands on ≥2 editable milestones
 * that share one date, this prompt names each milestone and lets the user pick which
 * one to move. Locked (reported) rows render disabled. A fully locked stack never
 * opens the picker (the parent gates on {@link stackOpensPicker}).
 */

import { useEffect, useState } from 'react';
import { Modal, RadioButton, RadioButtonGroup } from '@carbon/react';
import { milestoneTooltipLabel } from './utils';
import type { StackedMilestone } from './selection-model';
import type { MilestoneKey } from './types';
import styles from './stacked-point-picker.module.scss';

export interface StackedPointPickerProps {
  open: boolean;
  stack: StackedMilestone[];
  onPick: (field: MilestoneKey) => void;
  onClose: () => void;
}

/**
 * Renders the stacked-milestone picker modal.
 *
 * @param {StackedPointPickerProps} props - Open flag, the stack, and pick/close handlers.
 * @return {JSX.Element | null} The modal, or null when closed.
 */
export function StackedPointPicker({
  open,
  stack,
  onPick,
  onClose,
}: StackedPointPickerProps) {
  const firstEditable = stack.find((s) => !s.locked)?.field ?? null;
  const [selected, setSelected] = useState<MilestoneKey | null>(firstEditable);

  useEffect(() => {
    setSelected(stack.find((s) => !s.locked)?.field ?? null);
  }, [stack]);

  if (!open) {
    return null;
  }

  return (
    <Modal
      open
      size="sm"
      modalHeading="Several milestones share this date"
      primaryButtonText="Move milestone"
      secondaryButtonText="Cancel"
      primaryButtonDisabled={selected === null}
      onRequestClose={onClose}
      onRequestSubmit={() => {
        if (selected !== null) {
          onPick(selected);
        }
      }}
    >
      <p className={styles.hint}>Pick the milestone to move.</p>
      <RadioButtonGroup
        name="stacked-milestone"
        orientation="vertical"
        valueSelected={selected ?? undefined}
        onChange={(value) => setSelected(value as MilestoneKey)}
      >
        {stack.map((item) => (
          <RadioButton
            key={item.field}
            value={item.field}
            disabled={item.locked}
            labelText={milestoneTooltipLabel(item.field, item.date)}
          />
        ))}
      </RadioButtonGroup>
    </Modal>
  );
}
